import type { ComponentPropsWithoutRef, ComponentRef, HTMLAttributes, ReactNode, Ref } from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'

import cn from '@components/utils/cn'
import Icon, { type IconTypes } from '@components/utils/Icon'

const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

const DropdownMenuSubTrigger = ({
  className,
  inset,
  children,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>>
}) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'tw:flex tw:cursor-default tw:select-none tw:items-center tw:rounded-xs tw:px-2 tw:py-1.5 tw:text-sm tw:outline-hidden tw:data-highlighted:bg-primary-100 tw:data-highlighted:text-primary-900 tw:data-[state=open]:bg-primary-100 tw:data-[state=open]:text-primary-900',
      inset && 'tw:pl-8',
      className
    )}
    {...props}
  >
    {children}
    <Icon type="chevron-right" width={16} height={16} />
  </DropdownMenuPrimitive.SubTrigger>
)

const DropdownMenuSubContent = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.SubContent>>
}) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      'tw:z-50 tw:min-w-32 tw:max-h-(--radix-dropdown-menu-content-available-height) tw:overflow-y-auto tw:rounded-md tw:border-solid tw:border tw:border-gray-200 tw:bg-white tw:p-1 tw:text-gray-700 tw:shadow-lg tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0 tw:data-[state=closed]:zoom-out-95 tw:data-[state=open]:zoom-in-95 tw:data-[side=bottom]:slide-in-from-top-2 tw:data-[side=left]:slide-in-from-right-2 tw:data-[side=right]:slide-in-from-left-2 tw:data-[side=top]:slide-in-from-bottom-2',
      className
    )}
    {...props}
  />
)

const DropdownMenuContent = ({
  className,
  sideOffset = 4,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Content>>
}) => (
  <DropdownMenuPrimitive.Portal>
    <div className="tailwind">
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'tw:z-50 tw:min-w-32 tw:max-h-(--radix-dropdown-menu-content-available-height) tw:overflow-y-auto tw:rounded-md tw:border-solid tw:border tw:border-gray-200 tw:bg-white tw:p-1 tw:text-gray-700 tw:shadow-md tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0 tw:data-[state=closed]:zoom-out-95 tw:data-[state=open]:zoom-in-95 tw:data-[side=bottom]:slide-in-from-top-2 tw:data-[side=left]:slide-in-from-right-2 tw:data-[side=right]:slide-in-from-left-2 tw:data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        {...props}
      />
    </div>
  </DropdownMenuPrimitive.Portal>
)

const DropdownMenuItem = ({
  className,
  inset,
  destructive,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean
  destructive?: boolean
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Item>>
}) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'tw:relative tw:flex tw:cursor-pointer tw:select-none tw:items-center tw:gap-2 tw:rounded-xs',
      'tw:px-2 tw:py-1.5 tw:text-sm tw:outline-hidden tw:transition-colors',
      'tw:[&>svg]:h-3.5 tw:[&>svg]:w-3.5 tw:[&>svg]:shrink-0',
      'tw:data-highlighted:bg-primary-100 tw:data-highlighted:text-primary-900',
      'tw:data-disabled:pointer-events-none tw:data-disabled:opacity-50',
      inset && 'tw:pl-8',
      destructive && 'tw:text-red-700 tw:data-highlighted:bg-red-50 tw:data-highlighted:text-red-700',
      className
    )}
    {...props}
  />
)

/** A roomier item: icon chip, bold label and optional description. Pass `href` to render it as a link. */
const DropdownMenuIconItem = ({
  icon,
  label,
  description,
  href,
  className,
  ...props
}: Omit<ComponentPropsWithoutRef<typeof DropdownMenuItem>, 'children'> & {
  icon: IconTypes
  label: ReactNode
  description?: ReactNode
  href?: string
}) => {
  const content = (
    <>
      <span className="tw:flex tw:h-10 tw:w-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-transparent tw:bg-primary-50 tw:text-primary-600 tw:group-data-highlighted:border-primary-200 tw:group-data-highlighted:bg-white">
        <Icon type={icon} className="tw:h-5 tw:w-5" />
      </span>
      <span>
        <span className="tw:block tw:text-base tw:font-semibold tw:leading-tight tw:text-gray-cool-800">{label}</span>
        {description && (
          <span className="tw:mt-0.5 tw:block tw:text-sm tw:leading-snug tw:text-gray-cool-600">{description}</span>
        )}
      </span>
    </>
  )
  const itemClassName = cn(
    'tw:group tw:items-start tw:gap-3 tw:rounded-lg tw:p-3',
    'tw:data-highlighted:bg-primary-100',
    className
  )

  return href ? (
    <DropdownMenuItem asChild className={itemClassName} {...props}>
      <a href={href}>{content}</a>
    </DropdownMenuItem>
  ) : (
    <DropdownMenuItem className={itemClassName} {...props}>
      {content}
    </DropdownMenuItem>
  )
}

const DropdownMenuCheckboxItem = ({
  className,
  children,
  checked,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.CheckboxItem>>
}) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'tw:group tw:relative tw:flex tw:cursor-default tw:select-none tw:items-center tw:rounded-xs tw:py-1.5 tw:pl-8 tw:pr-2 tw:text-sm tw:outline-hidden tw:transition-colors tw:data-highlighted:bg-primary-100 tw:data-highlighted:text-primary-900 tw:data-disabled:pointer-events-none tw:data-disabled:opacity-50',
      className
    )}
    checked={checked}
    {...props}
  >
    {/* Matches ComboboxCheckItem so every multi-choice list shows the same checkbox. */}
    <span
      aria-hidden
      className="tw:absolute tw:left-2 tw:flex tw:h-4 tw:w-4 tw:items-center tw:justify-center tw:rounded tw:border tw:border-gray-300 tw:bg-white tw:transition-colors tw:group-data-[state=checked]:border-primary-500 tw:group-data-[state=checked]:bg-primary-50"
    >
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon type="check" width={12} height={12} className="tw:text-primary-500 tw:stroke-2" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
)

const DropdownMenuRadioItem = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.RadioItem>>
}) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'tw:relative tw:flex tw:cursor-default tw:select-none tw:items-center tw:rounded-xs tw:py-1.5 tw:pl-8 tw:pr-2 tw:text-sm tw:outline-hidden tw:transition-colors tw:data-highlighted:bg-primary-100 tw:data-highlighted:text-primary-900 tw:data-disabled:pointer-events-none tw:data-disabled:opacity-50',
      className
    )}
    {...props}
  >
    <span className="tw:absolute tw:left-2 tw:flex tw:h-3.5 tw:w-3.5 tw:items-center tw:justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon type="dot" width={8} height={8} />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
)

const DropdownMenuLabel = ({
  className,
  inset,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Label>>
}) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('tw:px-2 tw:py-1.5 tw:text-sm tw:font-semibold', inset && 'tw:pl-8', className)}
    {...props}
  />
)

const DropdownMenuSeparator = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Separator>>
}) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('tw:-mx-1 tw:my-1 tw:h-px tw:bg-gray-200', className)}
    {...props}
  />
)

const DropdownMenuShortcut = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('tw:ml-auto tw:text-xs tw:tracking-widest tw:opacity-60', className)} {...props} />
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuIconItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}
